/**
 * The quiet stats strip above the Clients table.
 *
 * "N clients · N documents · N scopes · N Polly chats". Under the client-hub
 * v2 coherence rule (superseding v1's org-wide-header decision) the last
 * three numbers match the sum of the corresponding per-client column:
 * documents/scopes count only client-tagged rows, and Polly chats count the
 * viewer's client-linked conversations PLUS the ones shared with them (the
 * Chats column; both widened in lockstep so the header still equals the sum).
 * `client_count` is the plain org client total. Silent on error: the table
 * is the page's job; a stats failure must not block it.
 */
import { useQuery } from "@tanstack/react-query";

import { getClientStats } from "@/api";

function Stat({ count, word }: { count: number; word: string }) {
    return (
        <>
            <span className="text-foreground font-medium">
                {count.toLocaleString()}
            </span>{" "}
            {word}
            {count === 1 ? "" : "s"}
        </>
    );
}

const Separator = () => <span className="mx-3">·</span>;

export function ClientStatsHeader() {
    const { data, isPending, isError } = useQuery({
        queryKey: ["clients-stats"],
        queryFn: getClientStats,
    });

    if (isError) return null;

    if (isPending) {
        return (
            <div
                className="flex items-center gap-2"
                aria-label="Loading client stats"
            >
                {[0, 1, 2, 3].map((i) => (
                    <span key={i} className="flex items-center gap-2">
                        {i > 0 && (
                            <span className="text-muted-foreground text-sm">
                                ·
                            </span>
                        )}
                        <span className="bg-muted h-4 w-16 animate-pulse rounded" />
                    </span>
                ))}
            </div>
        );
    }

    return (
        <p className="text-muted-foreground text-sm">
            <Stat count={data.client_count} word="client" />
            <Separator />
            <Stat count={data.document_count} word="document" />
            <Separator />
            <Stat count={data.scope_count} word="scope" />
            <Separator />
            <Stat count={data.conversation_count} word="Polly chat" />
        </p>
    );
}
